import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);
const CameraIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);
const ImageIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);
const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m4-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function formatDateTime(date: Date, locale: string): string {
  return date.toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' });
}

function calcDLC(days: number, hours: number): Date {
  const dlc = new Date();
  dlc.setHours(dlc.getHours() + days * 24 + hours);
  return dlc;
}

export function CreatePreparationRecetteForm({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [now, setNow] = useState(new Date());
  const [formData, setFormData] = useState({
    name: '',
    shelfLifeDays: '',
    shelfLifeHours: '',
    quantity: '',
    unit: 'kg',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Keep current time updated
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(interval);
  }, []);

  const days = parseInt(formData.shelfLifeDays || '0', 10) || 0;
  const hours = parseInt(formData.shelfLifeHours || '0', 10) || 0;
  const dlcPreview = calcDLC(days, hours);

  const createMutation = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append('name', formData.name.trim());
      fd.append('shelfLifeDays', String(days));
      fd.append('shelfLifeHours', String(hours));
      fd.append('quantity', formData.quantity);
      fd.append('unit', formData.unit);
      if (photo) fd.append('photo', photo);
      return apiClient.createRecipe(fd);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preparation-recettes'] });
      addToast(t('recettes.createSuccess'), 'success');
      onSuccess();
    },
    onError: () => {
      addToast(t('recettes.createError'), 'error');
    },
  });

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) { addToast(t('recettes.nameRequired'), 'error'); return; }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) { addToast(t('recettes.quantityRequired'), 'error'); return; }
    if (days === 0 && hours === 0) { addToast(t('recettes.durationRequired'), 'error'); return; }
    if (!photo) { addToast(t('recettes.photoRequired'), 'error'); return; }
    createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full my-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-slate-200 bg-white">
          <h2 className="text-xl font-bold text-slate-900">{t('recettes.formTitle')}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition">
            <XIcon className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Start time (auto) */}
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
            <ClockIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-700">{t('recettes.startTime')}</p>
              <p className="text-sm font-semibold text-blue-900">{formatDateTime(now, i18n.language)}</p>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('recettes.name')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder={t('recettes.namePlaceholder')}
            />
          </div>

          {/* Durée de vie */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('recettes.shelfLife')} *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={formData.shelfLifeDays}
                    onChange={(e) => setFormData({ ...formData, shelfLifeDays: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="text-sm text-slate-600 whitespace-nowrap">{t('recettes.days')}</span>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={formData.shelfLifeHours}
                    onChange={(e) => setFormData({ ...formData, shelfLifeHours: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="text-sm text-slate-600 whitespace-nowrap">{t('recettes.hours')}</span>
                </div>
              </div>
            </div>
            {(days > 0 || hours > 0) && (
              <div className="mt-2 flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg border border-red-200">
                <ClockIcon className="w-4 h-4 text-red-600 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium text-red-700">{t('recettes.dlcPreview')}</p>
                  <p className="text-sm font-bold text-red-900">{formatDateTime(dlcPreview, i18n.language)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('recettes.quantity')} *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="0"
              />
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="L">L</option>
                <option value="mL">mL</option>
                <option value="portions">{t('recettes.portions')}</option>
                <option value="pièces">{t('recettes.pieces')}</option>
              </select>
            </div>
          </div>

          {/* Photo */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {t('recettes.photo')} *
            </label>
            {photoPreview ? (
              <div className="relative group">
                <img src={photoPreview} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                  className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition rounded-lg"
                >
                  <TrashIcon className="w-6 h-6 text-white" />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col items-center justify-center px-3 py-5 border-2 border-dashed border-orange-400 bg-orange-50 rounded-lg cursor-pointer hover:bg-orange-100 transition">
                  <CameraIcon className="w-7 h-7 text-orange-600 mb-1" />
                  <span className="text-sm font-medium text-orange-700 text-center">{t('recettes.takePhoto')}</span>
                  <input type="file" accept="image/*" capture="environment" onChange={handlePhotoSelect} className="hidden" />
                </label>
                <label className="flex flex-col items-center justify-center px-3 py-5 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition">
                  <ImageIcon className="w-7 h-7 text-slate-400 mb-1" />
                  <span className="text-sm text-slate-600 text-center">{t('recettes.choosePhoto')}</span>
                  <input type="file" accept="image/*" onChange={handlePhotoSelect} className="hidden" />
                </label>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckIcon className="w-5 h-5" />
              {createMutation.isPending ? t('recettes.creating') : t('recettes.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
